import React, { useState, useEffect, lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { Toaster } from '@/components/ui/sonner'
import { ThemeProvider, useSyncAccountTheme } from '@/contexts/ThemeContext'
import { LoginScreen } from './components/auth/LoginScreenRouter'
import { PasswordRecoveryScreen } from './components/auth/PasswordRecoveryScreen'
import { PasswordResetScreen } from './components/auth/PasswordResetScreen'
import { GoogleCallback } from './components/auth/GoogleCallback'
import { PrivacyPolicy } from './components/legal/PrivacyPolicy'
import { TermsOfService } from './components/legal/TermsOfService'
import { Dashboard } from './components/dashboard/DashboardFinal'
import { CreateCampaignScreen } from './components/campaigns/CreateCampaignScreen'
import { ConfigureSystemScreen } from './components/campaigns/ConfigureSystemScreen'
import { JoinInviteScreen } from './components/campaigns/JoinInviteScreen'
import { TableDetails } from './components/tables/TableDetailsRouter'
import { GameSessionWrapper } from './components/game/GameSessionWrapper'
import { GameSession } from './components/game/GameSession'
import { ProfileScreen } from './components/account/ProfileScreenRouter'
import { Navigation } from './components/layout/NavigationRouter'
import { PageErrorBoundary } from './components/PageErrorBoundary'
import { AdminPanel } from './components/admin/AdminPanel'
import { EngagementPanel } from './components/admin/EngagementPanel'
import { ModuleLabModule } from './modules/module_lab/moduleLabModule'
import { BookIaModule } from './modules/book_ia/bookIaModule'

const BookIaRuntime = lazy(() => import('./modules/book_ia/shell/BookIaRuntime'))

/*
  O sandbox do atlas: SÓ NO DEV.

  Estas rotas são o que sobrou de 16 telas de demonstração que nasceram quando
  não havia separação entre dev e produção — a única máquina era esta. Em
  2026-07-27 nove delas foram apagadas (mockups de redesigns já entregues) e
  estas sete ficaram: os componentes de produção do atlas são cópias adaptadas
  destes, e a versão standalone é a referência de origem.

  O `import.meta.env.DEV ? lazy(...) : null` precisa ficar assim, inline: no
  build o Vite troca a expressão por `false`, o Rollup dobra a constante e o
  `import()` some junto com o pedaço inteiro — nenhum byte destas telas entra
  no bundle de produção, e nenhuma rota fica registrada (a rota `*` manda quem
  tentar de volta para a página inicial). Se alguém "simplificar" isto para uma
  função auxiliar que receba `() => import(...)`, o argumento passa a ser vivo,
  o Rollup deixa de podar e as telas voltam ao ar em `edurpg.ia.br`.

  Cuidado ao mexer no sandbox: `atlasLab.css` NÃO mora mais lá. Ele é o CSS
  canônico do atlas e foi para `src/modules/module_lab/games/atlas/`, junto dos
  sete componentes de produção que o usam; agora é o standalone que importa
  dele, e não o contrário.
*/
const NO_DEV = import.meta.env.DEV

const AtlasLabHome = NO_DEV ? lazy(() => import('./test/atlas-lab/AtlasLabHome').then(m => ({ default: m.AtlasLabHome }))) : null
const Reconhecimento = NO_DEV ? lazy(() => import('./test/atlas-lab/missoes/Reconhecimento').then(m => ({ default: m.Reconhecimento }))) : null
const Assinaturas = NO_DEV ? lazy(() => import('./test/atlas-lab/missoes/Assinaturas').then(m => ({ default: m.Assinaturas }))) : null
const Identificacao = NO_DEV ? lazy(() => import('./test/atlas-lab/missoes/Identificacao').then(m => ({ default: m.Identificacao }))) : null
const MassaInvariante = NO_DEV ? lazy(() => import('./test/atlas-lab/missoes/MassaInvariante').then(m => ({ default: m.MassaInvariante }))) : null
const HypatiaTutorial = NO_DEV ? lazy(() => import('./test/atlas-lab/missoes/HypatiaTutorial').then(m => ({ default: m.HypatiaTutorial }))) : null
const HypatiaReal = NO_DEV ? lazy(() => import('./test/atlas-lab/missoes/HypatiaReal').then(m => ({ default: m.HypatiaReal }))) : null

/** Caminho -> tela. Em produção cada tela é `null` e a rota não é registrada. */
const ROTAS_DE_DEMO: Array<[string, React.ComponentType | null]> = [
  ['/test/atlas-lab', AtlasLabHome],
  ['/test/atlas-lab/reconhecimento', Reconhecimento],
  ['/test/atlas-lab/assinaturas', Assinaturas],
  ['/test/atlas-lab/identificacao', Identificacao],
  ['/test/atlas-lab/massa', MassaInvariante],
  ['/test/atlas-lab/hypatia-tutorial', HypatiaTutorial],
  ['/test/atlas-lab/hypatia-real', HypatiaReal],
]

import { apiService } from './services/api'
import type { User } from './types'

interface DashboardWrapperProps {
  user?: User;
  onNavigateToTable: (tableId: string) => void;
}

const DashboardWrapper: React.FC<DashboardWrapperProps> = ({ user, onNavigateToTable }) => {
  if (!user) return null;
  return <Dashboard user={user} onNavigateToTable={onNavigateToTable} />;
};

const App = () => {
  return (
    <div className="min-h-screen bg-background">
      <Routes>
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/auth/google/success" element={<GoogleCallback />} />
        <Route path="/privacidade" element={<PrivacyPolicy />} />
        <Route path="/termos" element={<TermsOfService />} />
        <Route path="/recuperar-senha" element={<PasswordRecoveryScreen />} />
        <Route path="/redefinir-senha" element={<PasswordResetScreen />} />
        {/* Convite por link/QR: rota pública — trata login por conta própria */}
        <Route path="/convite/:code" element={<JoinInviteScreen />} />

        <Route
          path="/pagina-inicial"
          element={
            <PageErrorBoundary>
              <ProtectedRoute>
                <DashboardWrapper
                  onNavigateToTable={(tableId) => {
                    window.location.href = `/detalhes/${tableId}`;
                  }}
                />
              </ProtectedRoute>
            </PageErrorBoundary>
          }
        />
        <Route path="/campanhas/nova" element={<PageErrorBoundary><ProtectedRoute><CreateCampaignScreen /></ProtectedRoute></PageErrorBoundary>} />
        <Route path="/campanhas/:tableId/sistema" element={<PageErrorBoundary><ProtectedRoute><ConfigureSystemScreen /></ProtectedRoute></PageErrorBoundary>} />
        <Route path="/perfil" element={<ProtectedRoute><ProfileScreen /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
        <Route path="/engajamento" element={<ProtectedRoute><EngagementPanel /></ProtectedRoute>} />
        <Route path="/detalhes/:tableId" element={<PageErrorBoundary><ProtectedRoute><TableDetails /></ProtectedRoute></PageErrorBoundary>} />

        {/* solo_pbta desativado em 2026-06-10 (substituído pelo book_ia);
            código arquivado em _archive/solo_pbta/ */}

        {/* Rotas do module_lab — jogos epistêmicos mediados por mestre.
            requiredRole='any' permite aluno anônimo entrar sem JWT. */}
        {ModuleLabModule.routes?.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center" style={{ background: '#FAFAF7' }}><div className="animate-pulse">Carregando module_lab…</div></div>}>
                <route.component />
              </Suspense>
            }
          />
        ))}

        {/* Rotas do book_ia.
            requiredRole='any'      → sem auth (preview público).
            requiredRole='professor' → envolve em ProtectedRoute (config, etc.). */}
        {BookIaModule.routes?.map((route) =>
          route.requiredRole === 'any' ? (
            <Route
              key={route.path}
              path={route.path}
              element={
                <Suspense fallback={<div data-book-ia-root data-theme="dark"><div className="bia-status">Carregando book_ia…</div></div>}>
                  <route.component />
                </Suspense>
              }
            />
          ) : (
            <Route
              key={route.path}
              path={route.path}
              element={
                // Suspense FORA do ProtectedRoute: a página (route.component) é o
                // filho DIRETO do ProtectedRoute p/ receber `user` via cloneElement
                // (regra crítica — senão a página recebe user=undefined).
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-background"><div className="animate-pulse">Carregando…</div></div>}>
                  <ProtectedRoute>
                    <route.component />
                  </ProtectedRoute>
                </Suspense>
              }
            />
          )
        )}

        {/* Rota da sessão real do book_ia (tela cheia, protegida).
            Suspense FORA do ProtectedRoute: BookIaRuntime é filho DIRETO
            do ProtectedRoute para receber user via cloneElement (regra crítica).
            Wrappers adicionais (ErrorBoundary etc.) devem ir por fora também. */}
        <Route
          path="/mesa/:tableId/book_ia/:sessionId"
          element={
            <Suspense
              fallback={
                <div data-book-ia-root>
                  <div className="bia-status">Carregando book_ia…</div>
                </div>
              }
            >
              <ProtectedRoute fullScreen>
                <BookIaRuntime />
              </ProtectedRoute>
            </Suspense>
          }
        />

        <Route
          path="/game/:tableId"
          element={
            <ProtectedRoute fullScreen>
              <GameSessionWrapper />
            </ProtectedRoute>
          }
        />

        {/* Rota de teste para layout clássico direto */}
        <Route
          path="/game-classic/:tableId"
          element={
            <ProtectedRoute fullScreen>
              <GameSession />
            </ProtectedRoute>
          }
        />

        {/* Telas de demonstração — só existem no dev (ver o bloco no topo).
            Em produção a lista é toda de nulos, o react-router ignora filho
            que não é elemento, e a rota `*` cuida de quem tentar a URL. */}
        {ROTAS_DE_DEMO.map(([caminho, Tela]) => Tela && (
          <Route
            key={caminho}
            path={caminho}
            element={<Suspense fallback={<div className="p-8 text-sm">Carregando demo...</div>}><Tela /></Suspense>}
          />
        ))}

        <Route path="/" element={<Navigate to="/pagina-inicial" replace />} />
        <Route path="*" element={<Navigate to="/pagina-inicial" replace />} />
      </Routes>
      <Toaster />
    </div>
  )
}

const AppRouter = () => {
  return (
    <ThemeProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <App />
      </Router>
    </ThemeProvider>
  )
}

const ProtectedRoute: React.FC<{ children: React.ReactNode; fullScreen?: boolean }> = ({ children, fullScreen = false }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const location = useLocation()
  const navigate = useNavigate()

  // Backend e a fonte de verdade do tema da conta; alinha o cache local apos o auth.
  useSyncAccountTheme(user)

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('auth_token')
        if (!token) {
          setIsAuthenticated(false)
          return
        }
        const userData = await apiService.getCurrentUser()
        setUser(userData)
        setIsAuthenticated(true)
      } catch (error) {
        localStorage.removeItem('auth_token')
        setIsAuthenticated(false)
        setUser(null)
      }
    }
    checkAuth()
  }, [])

  if (isAuthenticated === false) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Se ainda está verificando autenticação e não tem usuário
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-xl font-semibold">Verificando...</div>
      </div>
    )
  }

  // Se está autenticado mas não tem dados do usuário, tentar buscar novamente
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  const content = React.Children.map(children, child =>
    React.isValidElement(child)
      ? React.cloneElement(child as React.ReactElement<any>, { user })
      : child
  )

  return (
    <div className={`min-h-screen flex flex-col ${fullScreen ? 'bg-[#0b141a]' : 'bg-background'}`}>
      {/* Navigation - ocultar quando fullScreen for true */}
      {!fullScreen && (
        <Navigation
          user={user}
          onLogout={() => {
            apiService.logout()
            navigate('/login')
          }}
        />
      )}

      {fullScreen ? (
        // Tela cheia sem container nem padding
        <div className="flex-1 min-h-0 w-full h-screen overflow-hidden">{content}</div>
      ) : (
        // Layout normal com container e padding
        <main className="container mx-auto px-4 py-8 flex-1 w-full">
          {content}
        </main>
      )}
    </div>
  )
}

export default AppRouter
